/**
 * useAccountIdentity — reactive read of the account attribution
 * (avatar + initial) for a single internal Account id.
 *
 * Backed by the shared `accountAttributionStore` Zustand map. The
 * store is primed elsewhere (`usePrimeAccountAttributionFromMembers`)
 * whenever effective collection members load — typically as a
 * by-product of any surface that resolves a block's lip. So by the
 * time a comment popover or rail note needs to paint an author's
 * avatar, the matching identity is already cached: this hook just
 * reads it.
 *
 * Falls back to placeholder initials (via `resolveAttributionIdentity`
 * with no members) when the store doesn't have an entry for the id.
 * That keeps the comment surfaces honest in flows where priming
 * hasn't happened yet — initials over no avatar over nothing.
 */
import { useMemo } from "react";
import { accountAttributionStore } from "@/data/account-attribution-store";
import { resolveAttributionIdentity, } from "@/components/block/lip-attribution";
export function useAccountIdentity(accountId) {
    // Subscribe to just this account's slice of the store. Returning
    // `undefined` when the accountId is empty/missing keeps the
    // selector idempotent for callers that don't have an id yet
    // (e.g. an optimistic comment whose ownerId hasn't been replaced
    // by the server-issued account id).
    const stored = accountAttributionStore((s) => accountId ? s.byAccountId[accountId] : undefined);
    // Memoize the placeholder so a missing-store-entry doesn't churn a
    // fresh `AttributionIdentity` object on every render. Prevents
    // memo'd consumers from re-rendering on unrelated parent updates.
    return useMemo(() => {
        if (stored)
            return stored;
        return resolveAttributionIdentity(accountId ?? "", undefined);
    }, [stored, accountId]);
}
